import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import AgingCriteriaModal, { defaultCriteria } from '../components/financials/AgingCriteriaModal';
import BpGoldenArrow from '../components/financials/BpGoldenArrow';
import { agingColumnLabels, buildAgingQuery } from '../utils/financialsAging';

/** en-IN style; negatives in parentheses like SAP / accounting */
const fmt = (v) => {
  if (v == null || Number.isNaN(v)) return '—';
  const n = Number(v);
  const abs = Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `(${abs})` : abs;
};

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

import { getApiBase } from '../config/apiBase';

const apiBase = getApiBase();

/** Table cell base — firm grid lines, theme border */
const C = {
  th: 'border border-sap-border/80 px-4 py-3 font-semibold text-2xs uppercase tracking-wider text-sap-textMuted bg-gray-50',
  thNum: 'border border-sap-border/80 px-4 py-3 font-semibold text-2xs uppercase tracking-wider text-sap-textMuted bg-gray-50 text-right',
  thTree: 'border border-sap-border/80 px-2 py-3 text-center font-semibold text-2xs uppercase tracking-wider text-sap-textMuted bg-gray-50',
  /** Customer summary (group header) */
  sum: 'border border-sap-border/80 px-2 py-2 bg-sap-blueLight/90 text-sap-text',
  sumNum: 'border border-sap-border/80 px-2 py-2 bg-sap-blueLight/90 text-right tabular-nums font-bold text-sap-text',
  sumTree: 'border border-sap-border/80 px-1 py-2 text-center align-middle bg-sap-blueLight/90',
  /** Detail line under a customer */
  det: 'border border-sap-border/80 px-2 py-1.5 bg-white text-sap-text',
  detAlt: 'border border-sap-border/80 px-2 py-1.5 bg-sap-bg/50 text-sap-text',
  detNum: 'border border-sap-border/80 px-2 py-1.5 text-right tabular-nums text-sap-text',
  detNumAlt: 'border border-sap-border/80 px-2 py-1.5 text-right tabular-nums bg-sap-bg/50 text-sap-text',
  detTree: 'border border-sap-border/80 px-1 py-1.5 text-center bg-sap-bg/80',
  detTreeAlt: 'border border-sap-border/80 px-1 py-1.5 text-center bg-sap-bg/40',
  foot: 'border border-sap-border/80 px-2 py-2 bg-sap-blue/10 font-bold text-sap-text',
  footNum: 'border border-sap-border/80 px-2 py-2 text-right tabular-nums font-bold text-sap-text bg-sap-blue/10',
  flat: 'border border-sap-border/80 px-2 py-1.5 text-sap-text',
  flatNum: 'border border-sap-border/80 px-2 py-1.5 text-right tabular-nums text-sap-text',
};

/** Stable key when grouping rows from the aging API (same customer may share code+name). */
function groupKey(r) {
  return `${String(r.bp_code ?? '')}\t${String(r.bp_name ?? '')}`;
}

function sumNumeric(lines, field) {
  return lines.reduce((s, r) => s + (Number(r[field]) || 0), 0);
}

/** Builds SAP-style customer → open documents groups. Order follows API (sorted by customer, then SO). */
function buildCustomerGroups(rows) {
  const order = [];
  const map = new Map();
  for (const r of rows) {
    const k = groupKey(r);
    if (!map.has(k)) {
      map.set(k, []);
      order.push(k);
    }
    map.get(k).push(r);
  }
  return order.map((key) => {
    const lines = map.get(key);
    const first = lines[0];
    return {
      key,
      bp_code: first.bp_code,
      bp_name: first.bp_name,
      bp_lookup_code: first.bp_lookup_code,
      blanket_agreement: first.blanket_agreement || '',
      lines,
    };
  });
}

function aggregateTotals(lines, colKeys) {
  const totals = {};
  colKeys.forEach((k) => { totals[k] = 0; });
  lines.forEach((r) => {
    colKeys.forEach((k) => {
      totals[k] += Number(r[k]) || 0;
    });
  });
  return totals;
}

/** Colspan from first column through BP Ref. No. (label cell for footer / empty cells for SAP detail) */
function leadColSpan(groupedView) {
  return groupedView ? 11 : 9;
}

export default function CustomerReceivablesAging() {
  const navigate = useNavigate();
  const [criteria, setCriteria] = useState(() => defaultCriteria('customer'));
  const [criteriaOpen, setCriteriaOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [intervals, setIntervals] = useState([30, 60, 90, 120]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [applied, setApplied] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  /** Bust browser cache for PDF iframe / ensure fresh render after server deploy */
  const [previewBust, setPreviewBust] = useState(0);
  /** Group keys with detail hidden (like SAP collapsed node). Absent = expanded. */
  const [collapsedCustomers, setCollapsedCustomers] = useState(() => new Set());

  const limit = 200;

  const load = useCallback(async () => {
    if (!applied) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const qs = buildAgingQuery(criteria, { page, limit, search });
      const res = await api.get(`/financials/aging/customer-receivables?${qs}`);
      setRows(res?.rows || []);
      setTotal(res?.total || 0);
      setIntervals(res?.intervals || [30, 60, 90, 120]);
      setCollapsedCustomers(new Set());
    } catch (e) {
      setRows([]);
      setTotal(0);
      setLoadError(e?.message || 'Failed to load aging data. Check that the server was restarted and Financials access is enabled.');
    } finally {
      setLoading(false);
    }
  }, [criteria, page, limit, search, applied]);

  useEffect(() => {
    load();
  }, [load]);

  const cols = agingColumnLabels(intervals);
  const groupLabel = criteria.groupBy === 'sales_employee' ? 'Sales Employee' : 'Customer';
  const totalPages = Math.ceil(total / limit) || 1;

  const totalKeys = useMemo(() => [...cols.map((c) => c.key), 'total'], [cols]);

  const customerGroups = useMemo(() => {
    if (criteria.groupBy !== 'customer' || !rows.length) return null;
    return buildCustomerGroups(rows);
  }, [criteria.groupBy, rows]);

  /** One footer row: sums all open lines in the current view (checks against customer subtotals). */
  const pageGrandTotals = useMemo(() => {
    if (!rows.length) return null;
    const buckets = aggregateTotals(rows, totalKeys);
    return {
      balance_due: sumNumeric(rows, 'balance_due'),
      future_remit: sumNumeric(rows, 'future_remit'),
      ...buckets,
    };
  }, [rows, totalKeys]);

  const downloadPdf = () => {
    if (!applied) return;
    const qs = buildAgingQuery(criteria, { page: 1, limit: 5000, search });
    window.open(`${apiBase}/financials/aging/customer-receivables/pdf?${qs}&download=1&_ts=${Date.now()}`, '_blank');
  };
  const downloadExcel = () => {
    if (!applied) return;
    const qs = buildAgingQuery(criteria, { page: 1, limit: 5000, search });
    window.open(`${apiBase}/financials/aging/customer-receivables/excel?${qs}&_ts=${Date.now()}`, '_blank');
  };
  const previewPdfUrl = useMemo(() => {
    if (!applied) return '';
    const q = buildAgingQuery(criteria, { page: 1, limit: 5000, search });
    return `${apiBase}/financials/aging/customer-receivables/pdf?${q}&download=0&_ts=${previewBust}`;
  }, [applied, criteria, search, previewBust]);

  const openBpMaster = useCallback(async (r) => {
    if (!r?.bp_lookup_code) return;
    try {
      const d = await api.get(`/business-partners/drill-down?code=${encodeURIComponent(r.bp_lookup_code)}`);
      if (d.variant === 'vendor') {
        navigate(`/vendor/${d.vendor_id}`);
      } else {
        const code = encodeURIComponent(d.customer_ref_no || r.bp_lookup_code);
        navigate(`/financials/customer-bp?code=${code}`);
      }
    } catch (e) {
      window.alert(e?.message || 'Could not resolve Business Partner for this code.');
    }
  }, [navigate]);

  const showBpArrow = criteria.groupBy === 'customer';

  const toggleGroup = useCallback((key) => {
    setCollapsedCustomers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setCollapsedCustomers(new Set());
  }, []);

  const collapseAll = useCallback(() => {
    if (!customerGroups) return;
    setCollapsedCustomers(new Set(customerGroups.map((g) => g.key)));
  }, [customerGroups]);

  const openSalesDoc = useCallback((soId) => {
    if (soId == null) return;
    navigate(`/sales-order/${soId}`);
  }, [navigate]);

  const groupedView = !!customerGroups?.length && criteria.groupBy === 'customer';

  const flatColCount = 14 + cols.length;
  const leadSpan = leadColSpan(groupedView);

  return (
    <div className="space-y-4 font-sap">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="page-title">Customer Receivables Aging</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => { setPreviewBust(Date.now()); setPreviewOpen(true); }}
            disabled={!applied}
            className="btn-secondary text-xs py-1.5 px-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            PDF
          </button>
          <button
            type="button"
            onClick={() => setCriteriaOpen(true)}
            className="btn-primary"
          >
            Selection Criteria
          </button>
        </div>
      </div>

      {loadError && (
        <div className="px-3 py-2 rounded-lg text-xs bg-amber-50 border border-sap-warning/40 text-sap-text">
          {loadError}
        </div>
      )}

      <div className="flex gap-3 items-center flex-wrap">
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder={`Search ${groupLabel.toLowerCase()}…`}
          className="search-input flex-1 min-w-[140px]"
        />
        {groupedView && (
          <div className="flex items-center gap-2 text-2xs shrink-0">
            <button
              type="button"
              onClick={expandAll}
              className="text-sap-blue font-medium hover:underline"
            >
              Expand all
            </button>
            <span className="text-sap-border">|</span>
            <button
              type="button"
              onClick={collapseAll}
              className="text-sap-blue font-medium hover:underline"
            >
              Collapse all
            </button>
          </div>
        )}
      </div>

      <div className="data-table-wrap overflow-x-auto">
        <table className={`w-full text-xs border-collapse ${groupedView ? 'min-w-[1560px]' : 'min-w-[1500px]'}`}>
          <thead>
            <tr>
              {groupedView && (
                <>
                  <th className={`${C.thTree} w-8`} aria-label="Expand" />
                  <th className={`${C.thTree} w-9`}>#</th>
                </>
              )}
              <th className={`${C.th} text-left`}>Customer Code</th>
              <th className={`${C.th} text-left`}>{groupLabel} Name</th>
              <th className={`${C.th} text-left`}>Blanket Agreement</th>
              <th className={`${C.th} text-center`}>Type</th>
              <th className={`${C.th} text-left`}>Doc. No.</th>
              <th className={`${C.th} text-center`}>Instal. No.</th>
              <th className={`${C.th} text-center`}>Posting Date</th>
              <th className={`${C.th} text-center`}>Due Date</th>
              <th className={`${C.th} text-left`}>BP Ref. No.</th>
              <th className={C.thNum}>Balance Due</th>
              <th className={C.thNum}>Future Remit</th>
              <th className={`${C.th} text-left`}>Dunning Term</th>
              {cols.map((c) => (
                <th key={c.key} className={`${C.thNum} whitespace-nowrap`}>{c.label}</th>
              ))}
              <th className={C.thNum}>Total</th>
              <th className={`${C.th} text-center`}>Currency</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={flatColCount + (groupedView ? 2 : 0)} className="border border-sap-border px-3 py-8 text-center text-sap-textLight">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={flatColCount + (groupedView ? 2 : 0)} className="border border-sap-border px-3 py-8 text-center text-sap-textLight">
                  {applied ? 'No open receivables for the current criteria.' : 'Open Selection Criteria, choose Aging Date, then click OK.'}
                </td>
              </tr>
            ) : groupedView ? (
              (() => {
                let lineNo = 0;
                return customerGroups.map((g) => {
                  const open = !collapsedCustomers.has(g.key);
                  const totals = aggregateTotals(g.lines, totalKeys);
                  const bd = sumNumeric(g.lines, 'balance_due');
                  const fr = sumNumeric(g.lines, 'future_remit');

                  const summaryCells = (
                    <>
                      <td className={`${C.sumTree} ${open ? '' : 'border-b-2 border-sap-border/90'}`}>
                        <button
                          type="button"
                          aria-expanded={open}
                          title={open ? 'Collapse document lines' : 'Expand document lines'}
                          onClick={() => toggleGroup(g.key)}
                          className="inline-flex w-7 h-7 items-center justify-center rounded text-sap-blue hover:bg-sap-blue/10"
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            className={`transition-transform duration-150 ${open ? 'rotate-0' : '-rotate-90'}`}
                            fill="currentColor"
                            aria-hidden
                          >
                            <path d="M6 4l6 4-6 4V4z" />
                          </svg>
                        </button>
                      </td>
                      <td className={`${C.sumTree} text-sap-textLight text-2xs border-b-sap-border/90 ${open ? '' : 'border-b-2'}`}>—</td>
                      <td className={`${C.sum} font-semibold border-b-sap-border/90 ${open ? '' : 'border-b-2'}`}>
                        <div className="flex items-center gap-1 min-w-0">
                          <span className="truncate">{g.bp_code || '—'}</span>
                          {showBpArrow && g.bp_lookup_code && (
                            <BpGoldenArrow title="Business Partner Master Data" onClick={() => openBpMaster(g)} />
                          )}
                        </div>
                      </td>
                      <td className={`${C.sum} font-semibold border-b-sap-border/90 ${open ? '' : 'border-b-2'}`}>
                        <div className="flex items-center gap-1 min-w-0">
                          <span className="truncate">{g.bp_name || '—'}</span>
                          {showBpArrow && g.bp_lookup_code && (
                            <BpGoldenArrow title="Business Partner Master Data" onClick={() => openBpMaster(g)} />
                          )}
                        </div>
                      </td>
                      <td className={`${C.sum} text-sap-textLight border-b-sap-border/90 ${open ? '' : 'border-b-2'}`}>{g.blanket_agreement || '—'}</td>
                      <td className={`${C.sum} text-center text-sap-textLight border-b-sap-border/90 ${open ? '' : 'border-b-2'}`}>—</td>
                      <td className={`${C.sum} border-b-sap-border/90 ${open ? '' : 'border-b-2'}`}>
                        {g.lines.length === 1 ? (
                          <span className="text-sap-text">{g.lines[0].doc_no || '—'}</span>
                        ) : (
                          <span className="text-sap-textLight">{g.lines.length} open lines</span>
                        )}
                      </td>
                      <td className={`${C.sum} text-center text-sap-textLight border-b-sap-border/90 ${open ? '' : 'border-b-2'}`}>—</td>
                      <td className={`${C.sum} text-center text-sap-textLight border-b-sap-border/90 ${open ? '' : 'border-b-2'}`}>—</td>
                      <td className={`${C.sum} text-center text-sap-textLight border-b-sap-border/90 ${open ? '' : 'border-b-2'}`}>—</td>
                      <td className={`${C.sum} text-sap-textLight truncate max-w-[120px] border-b-sap-border/90 ${open ? '' : 'border-b-2'}`} title={g.lines[0]?.bp_ref_no}>
                        {g.lines[0]?.bp_ref_no || '—'}
                      </td>
                      <td className={`${C.sumNum} border-b-sap-border/90 ${open ? '' : 'border-b-2'}`}>{fmt(bd)}</td>
                      <td className={`${C.sumNum} border-b-sap-border/90 ${open ? '' : 'border-b-2'}`}>{fmt(fr)}</td>
                      <td className={`${C.sum} border-b-sap-border/90 ${open ? '' : 'border-b-2'}`}>{g.lines[0]?.dunning_term_code || 'Standard'}</td>
                      {cols.map((c) => (
                        <td key={c.key} className={`${C.sumNum} border-b-sap-border/90 ${open ? '' : 'border-b-2'}`}>{fmt(totals[c.key])}</td>
                      ))}
                      <td className={`${C.sumNum} border-b-sap-border/90 ${open ? '' : 'border-b-2'}`}>{fmt(totals.total)}</td>
                      <td className={`${C.sum} text-center font-medium border-b-sap-border/90 ${open ? '' : 'border-b-2'}`}>{g.lines[0]?.currency || '—'}</td>
                    </>
                  );

                  const detailRows = open
                    ? g.lines.map((r, li) => {
                      lineNo += 1;
                      const rowAlt = li % 2 === 0;
                      const d = rowAlt ? C.det : C.detAlt;
                      const n = rowAlt ? C.detNum : C.detNumAlt;
                      const t = rowAlt ? C.detTree : C.detTreeAlt;
                      return (
                        <tr key={r.row_id}>
                          <td className={`${t} border-l-[3px] border-l-sap-blue/40`} />
                          <td className={`${t} text-sap-textLight text-2xs tabular-nums border-l-transparent`}>{lineNo}</td>
                          <td colSpan={2} className={`${d} pl-3 text-sap-textLight italic border-l-transparent`}>
                            <span className="sr-only">Lines for {g.bp_code}</span>
                            —
                          </td>
                          <td className={d}>{r.blanket_agreement || '—'}</td>
                          <td className={`${d} text-center`}>
                            <div className="inline-flex items-center justify-center gap-0.5">
                              <span>{r.type || '—'}</span>
                              {r.so_id != null && (
                                <BpGoldenArrow title="Open sales document" onClick={() => openSalesDoc(r.so_id)} />
                              )}
                            </div>
                          </td>
                          <td className={d}>
                            {r.so_id != null ? (
                              <button
                                type="button"
                                title="Open source document"
                                onClick={() => openSalesDoc(r.so_id)}
                                className="font-medium text-sap-blue hover:underline tabular-nums"
                              >
                                {r.doc_no || '—'}
                              </button>
                            ) : (
                              <span className="font-medium text-sap-blue">{r.doc_no || '—'}</span>
                            )}
                          </td>
                          <td className={`${d} text-center`}>{r.instal_no ?? '—'}</td>
                          <td className={`${d} text-center tabular-nums text-sap-textLight`}>{fmtDate(r.posting_date)}</td>
                          <td className={`${d} text-center tabular-nums text-sap-textLight`}>{fmtDate(r.due_date)}</td>
                          <td className={d}>{r.bp_ref_no || '—'}</td>
                          <td className={n}>{fmt(r.balance_due)}</td>
                          <td className={n}>{fmt(r.future_remit)}</td>
                          <td className={d}>{r.dunning_term_code || '—'}</td>
                          {cols.map((c) => (
                            <td key={c.key} className={n}>{fmt(r[c.key])}</td>
                          ))}
                          <td className={`${n} font-semibold`}>{fmt(r.total)}</td>
                          <td className={`${d} text-center`}>{r.currency || '—'}</td>
                        </tr>
                      );
                    })
                    : null;

                  return (
                    <React.Fragment key={g.key}>
                      <tr className="border-b border-sap-border">{summaryCells}</tr>
                      {detailRows}
                      {open && detailRows?.length ? (
                        <tr aria-hidden className="h-1">
                          <td colSpan={flatColCount + 2} className="border-0 p-0 bg-sap-bg/80" />
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                });
              })()
            ) : (
              rows.map((r, ri) => (
                <tr key={r.row_id} className={ri % 2 === 0 ? 'bg-white' : 'bg-sap-bg/40'}>
                  <td className={`${C.flat} border-l-[3px] border-l-transparent`}>
                    <div className="flex items-center gap-1 min-w-0">
                      <span className="font-medium truncate">{r.bp_code || '—'}</span>
                      {showBpArrow && r.bp_lookup_code && (
                        <BpGoldenArrow title="Business Partner Master Data" onClick={() => openBpMaster(r)} />
                      )}
                    </div>
                  </td>
                  <td className={C.flat}>
                    <div className="flex items-center gap-1 min-w-0">
                      <span className="truncate">{r.bp_name || '—'}</span>
                      {showBpArrow && r.bp_lookup_code && (
                        <BpGoldenArrow title="Business Partner Master Data" onClick={() => openBpMaster(r)} />
                      )}
                    </div>
                  </td>
                  <td className={C.flat}>{r.blanket_agreement || '—'}</td>
                  <td className={`${C.flat} text-center`}>
                    <div className="inline-flex items-center justify-center gap-0.5">
                      <span>{r.type || '—'}</span>
                      {r.so_id != null && (
                        <BpGoldenArrow title="Open sales document" onClick={() => openSalesDoc(r.so_id)} />
                      )}
                    </div>
                  </td>
                  <td className={C.flat}>
                    {r.so_id != null ? (
                      <button type="button" onClick={() => openSalesDoc(r.so_id)} className="font-medium text-sap-blue hover:underline">
                        {r.doc_no || '—'}
                      </button>
                    ) : (
                      <span className="font-medium text-sap-blue">{r.doc_no || '—'}</span>
                    )}
                  </td>
                  <td className={`${C.flat} text-center`}>{r.instal_no ?? '—'}</td>
                  <td className={`${C.flat} text-center text-sap-textLight`}>{fmtDate(r.posting_date)}</td>
                  <td className={`${C.flat} text-center text-sap-textLight`}>{fmtDate(r.due_date)}</td>
                  <td className={C.flat}>{r.bp_ref_no || '—'}</td>
                  <td className={C.flatNum}>{fmt(r.balance_due)}</td>
                  <td className={C.flatNum}>{fmt(r.future_remit)}</td>
                  <td className={C.flat}>{r.dunning_term_code || '—'}</td>
                  {cols.map((c) => (
                    <td key={c.key} className={C.flatNum}>{fmt(r[c.key])}</td>
                  ))}
                  <td className={`${C.flatNum} font-semibold`}>{fmt(r.total)}</td>
                  <td className={`${C.flat} text-center`}>{r.currency || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
          {applied && !loading && rows.length > 0 && pageGrandTotals && (
            <tfoot>
              <tr className="border-t-2 border-sap-blue/50">
                <td colSpan={leadSpan} className={`${C.foot} text-left`}>
                  Grand total <span className="font-normal text-sap-textLight">(sum of lines in this view)</span>
                </td>
                <td className={C.footNum}>{fmt(pageGrandTotals.balance_due)}</td>
                <td className={C.footNum}>{fmt(pageGrandTotals.future_remit)}</td>
                <td className={`${C.foot} text-sap-textLight`}>—</td>
                {cols.map((c) => (
                  <td key={c.key} className={C.footNum}>{fmt(pageGrandTotals[c.key])}</td>
                ))}
                <td className={C.footNum}>{fmt(pageGrandTotals.total)}</td>
                <td className={`${C.foot} text-center text-sap-textLight`}>—</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {applied && !loading && (
        <div className="flex items-center justify-between text-xs text-sap-textLight">
          <span>
            {total} line{total !== 1 ? 's' : ''}
            {groupedView && customerGroups ? ` · ${customerGroups.length} customer${customerGroups.length !== 1 ? 's' : ''}` : ''}
          </span>
          <div className="flex gap-1 items-center">
            <button
              type="button"
              disabled={page === 1 || totalPages <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-2.5 py-1 rounded-lg border border-sap-border disabled:opacity-40 hover:bg-sap-blue/5 transition-colors text-sap-text"
            >
              ‹
            </button>
            <span className="px-2.5 py-1 tabular-nums">{page} / {totalPages}</span>
            <button
              type="button"
              disabled={page === totalPages || totalPages <= 1}
              onClick={() => setPage((p) => p + 1)}
              className="px-2.5 py-1 rounded-lg border border-sap-border disabled:opacity-40 hover:bg-sap-blue/5 transition-colors text-sap-text"
            >
              ›
            </button>
          </div>
        </div>
      )}

      <AgingCriteriaModal
        open={criteriaOpen}
        variant="customer"
        value={criteria}
        onClose={() => setCriteriaOpen(false)}
        onApply={(next) => { setCriteria(next); setPage(1); setApplied(true); }}
      />

      {previewOpen && (
        <div className="fixed inset-0 z-[250] bg-black/45 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl h-[88vh] flex flex-col overflow-hidden border border-sap-border">
            <div className="px-4 py-2 border-b border-sap-border flex items-center justify-between bg-sap-bg/50">
              <div className="page-title">PDF Preview - Customer Receivables Aging</div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={downloadPdf} className="px-3 py-1 text-xs bg-sap-blue text-white rounded hover:bg-sap-blueDark font-semibold">Download</button>
                <button type="button" onClick={downloadExcel} className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 font-semibold">Download Excel</button>
                <button type="button" onClick={() => setPreviewOpen(false)} className="px-3 py-1 text-xs bg-slate-600 text-white rounded hover:bg-slate-700 font-semibold">Close</button>
              </div>
            </div>
            <iframe title="Customer Aging PDF Preview" key={previewBust} src={previewPdfUrl} className="w-full flex-1 border-0" />
          </div>
        </div>
      )}
    </div>
  );
}
